/**
 * runChatStream — core (A): the SSE `for await (...openChatStream...)` loop +
 * finalizeAssistantMessage + the error/abort recovery catch + the core-A finally
 * cleanup.
 *
 * Lifted VERBATIM from useChat.sendMessage (useChat.ts L726-L2351,
 * pre-decomposition): the markInterleaved/markXaviorStatus closures, the
 * finalizeAssistantMessage assembler, the `try { for await … }` stream dispatch,
 * the `catch` recovery gates, and the part of `finally` that resets streaming UI
 * state + the abort controller. The send-queue bookkeeping (`resolveTurn`,
 * `inFlightTurnRef`, `onStreamEnd`) stays in sendMessage's shell, which wraps the
 * call to runChatStream in its own try/finally — so the inner (core-A) finally
 * still runs BEFORE the queue finally, preserving the inline ordering.
 *
 * This is imperative turn-logic, NOT a React hook. It mutates the shared
 * {@link TurnContext} BY REFERENCE — every read/write of the 16 turn-local
 * primitives + the 2 Maps goes through `ctx.<field>`, every stable value through
 * `deps.<field>`. The ui-action `report.*` / `html.*` switch is delegated to
 * {@link applyUiDirective} (core B), which mutates the SAME ctx.
 *
 * Behavior is pinned by the characterization net:
 *   - useChat.stream-loop-char.test.ts   (loop ordering + turn-local transitions)
 *   - useChat.finalize-char.test.ts      (finalize content assembly)
 *   - useChat.ui-directive-char.test.ts  (report/html switch via applyUiDirective)
 *   - useChat.html-stream-char.test.ts   (html sub-handlers)
 *   - useChat.error-abort-char.test.ts   (catch-block recovery + abort cleanup)
 */

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query/keys'
import { getToolLabel } from '../utils/chat-tool-labels'
import { extractHiddenDirectives } from '../utils/hidden-directives'
import {
  appendAssistantChunk,
  waitForXaviorStatusVisibility,
  isGenericThinkingLabel,
  isUninformativeToolLabel,
} from '../utils/stream-text'
import { assembleFinalizeContent } from './helpers/finalize-content'
import { deriveReportResultCardActions } from './helpers/report-result-card'
import { deriveArtifactCardActions } from './helpers/artifact-result-card'
import { getUserFriendlyErrorMessage, TOUR_CHIP_FALLBACK } from '../utils/chat-errors'
import { extractCreatedObjectId } from '../utils/message-mapping'
import {
  setLiveHtml as setLiveHtmlState,
  clearLiveHtml as clearLiveHtmlState,
} from '@/lib/html-live-state'
import { toCanonicalPageId } from '@/lib/page-id'
import { scheduleObjectListInvalidation, isTrackedMutation } from '@/features/chat/utils/pendingCreateRegistry'
import { parseAgentActionReceipt } from '@xavior/contracts'
import { applyUiDirective } from './applyUiDirective'
import { endsWithQuestion } from '../utils/hidden-directives'
import type { ChatUIAction } from '@/lib/api/endpoints/chat'
import type { ChatMessage, QuickAction } from './useChat.types'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

/**
 * Grace window (ms) before a `deleted` effect's list invalidation refetches and
 * removes the highlighted chip. Gives the delete highlight (red wash +
 * `agent-fx-deleting`) time to paint on the still-mounted chip before the refetch
 * unmounts it and `<AnimatePresence>` plays the ~180ms collapse exit. Without it
 * the immediate invalidation yanks the chip in the same tick the effect ingests,
 * dropping the highlight (always for a whole-series delete, where every occurrence
 * chip is removed at once). Kept short so the row doesn't linger after the agent
 * has confirmed the delete.
 */
const DELETE_HIGHLIGHT_GRACE_MS = 700

/**
 * Dedupe follow-up chips by label (first occurrence wins), preserving order.
 * The primary chip-commit path concatenates two feeder sources (server-emitted
 * `actions` directives + finalize-time text extraction) that can carry the same
 * chip, and a tool may emit its follow-ups more than once in a turn — without
 * this the same chip renders twice (Bug #1). Mirrors the label-dedup contract of
 * `appendQuickActions` (utils/import-followups) but collapses a single list.
 */
export function dedupeQuickActionsByLabel(actions: QuickAction[]): QuickAction[] {
  const seen = new Set<string>()
  const out: QuickAction[] = []
  for (const action of actions) {
    if (seen.has(action.label)) continue
    seen.add(action.label)
    out.push(action)
  }
  return out
}

/**
 * Run the chat stream for a single turn. Builds the markInterleaved/markXavior
 * closures over `ctx`, exposes finalizeAssistantMessage as a local closure, and
 * drives the SSE loop. All side effects go through `deps`. Mutates `ctx` in
 * place. Returns when the turn fully completes (stream done / error / abort).
 */
export async function runChatStream(ctx: TurnContext, deps: ChatStreamDeps): Promise<void> {
  const {
    content,
    request,
    targetConversationId,
    requestStartTime,
    assistantId,
    assistantThinking,
    hideAssistantResponse,
    toolCalls,
    toolResults,
    queuedUiActions,
    queuedQuickActions,
    pendingCreates,
    isTargetConversationActive,
    token,
    workspaceId,
    mode,
    queryClient,
    activePageContextRef,
    profileRef,
    focusedSectionsRef,
    streamActivityRef,
    streamingContentRef,
    lastStreamEventAtRef,
    lastSendRef,
    abortControllerRef,
    sendInFlightRef,
    setThinking,
    setIsStreaming,
    setIsLoading,
    setMessages,
    setStreamingMessage,
    setError,
    setRetryBubble,
    setPendingQuickActions,
    setFocusedSections,
    pushActivity,
    pushThinkingStatus,
    clearActivity,
    onMessage,
    onQuickAck,
    onToolActivity,
    onObjectCreated,
    onStreamTextChunk,
    onError,
    onComplete,
    onUiActions,
    ingestAgentReceipt,
    markAgentCreatedEffect,
  } = deps

  const markInterleavedActivity = () => {
    ctx.sawInterleavedActivitySinceLastText = true
  }
  const markXaviorStatusActivity = () => {
    ctx.sawInterleavedActivitySinceLastText = true
    ctx.sawXaviorStatusSinceLastText = true
    ctx.lastXaviorStatusAtMs = Date.now()
  }

  // Object types this turn actually created OR updated (successful
  // create_object / update_object). Reported to silent-turn initiators (the
  // Daily Brief) so they can name the real destination — "Calendar updated"
  // only if an event was truly created or amended.
  const createdObjectTypes: string[] = []

  const finalizeAssistantMessage = (finalContent: string, meta?: {
    durationMs?: number
    routing?: string
    model?: string
    uiActions?: ChatUIAction[]
    quickActions?: QuickAction[]
  }) => {
    if (ctx.messageFinalized) return
    ctx.messageFinalized = true
    // Clear dedup guard so the user can send the same content again
    // (e.g., "yes" to confirm a different agent prompt). Without this,
    // repeated short confirmations are silently dropped for 60s (#1655).
    lastSendRef.current = null
    ctx.responseDurationMs = meta?.durationMs ?? ctx.responseDurationMs
    ctx.routingInfo = meta?.routing ?? ctx.routingInfo
    ctx.modelInfo = meta?.model ?? ctx.modelInfo
    // Pure content-assembly extracted to helpers/finalize-content.ts
    // (Pass-2 decomposition). Same helper calls, same order, same
    // predicates — byte-for-byte; pinned by useChat.finalize-char.test.ts.
    // Side effects (setMessages / onUiActions / persistence) + the
    // messageFinalized / lastSendRef / meta-merge bookkeeping stay here.
    const {
      finalVisibleContent,
      uiActionsWithAnchors,
      finalQuickActions,
      resultCards,
    } = assembleFinalizeContent({
      finalContent,
      assistantContent: ctx.assistantContent,
      assistantId,
      toolsUsedCount: ctx.toolsUsedCount,
      // End-of-report CTA (#3793): the kind:'report' result.card is derived in
      // the 'complete' handler's report-finalize block (BEFORE the registry is
      // cleared) and arrives here via queuedUiActions → meta.uiActions. It is
      // deliberately NOT derived here: by the time finalize runs on the
      // complete/disconnect paths the registry is already cleared, and on the
      // abort/error-fallback paths the report is partial/errored — carding it
      // would be a dead CTA (PR #3966 review). Card policy per finalize path:
      //   complete → card (≥1 section); abort/disconnect/error-fallback → no card.
      metaUiActions: meta?.uiActions || [],
      toolCalls,
      toolResults,
      activeHtmlModuleKeys: Array.from(ctx.activeHtmlModules.keys()),
    })

    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: finalVisibleContent,
      timestamp: new Date(),
      thinking: assistantThinking || undefined,
      toolsUsed: ctx.toolsUsedCount > 0 ? ctx.toolsUsedCount : undefined,
      routing: ctx.routingInfo || undefined,
      model: ctx.modelInfo || undefined,
      durationMs: ctx.responseDurationMs,
      mode,
    }

    // Silent turns (hidden from chat, e.g. the Daily Brief quick-capture) still
    // need their reply forwarded to the initiating modal — and that must happen
    // even if the user has since switched conversations (the modal lives outside
    // the chat pane). So dispatch BEFORE the isTargetConversationActive() gate,
    // which only governs chat-pane UI updates.
    if (hideAssistantResponse) {
      try {
        window.dispatchEvent(
          new CustomEvent('xavior:silent-response', {
            detail: { text: finalVisibleContent, createdTypes: [...createdObjectTypes] },
          }),
        )
      } catch { /* no-op */ }
    }

    if (isTargetConversationActive()) {
      setStreamingMessage(null)
      if (!hideAssistantResponse) {
        if (finalVisibleContent) {
          setMessages((prev) => [...prev, assistantMessage])
        }
        // Bug #1 (duplicate chips): the two feeder sources — server-emitted
        // `{{XAVIOR_ACTIONS}}` / `{type:'actions'}` directives (queuedQuickActions,
        // arriving via meta.quickActions) and the finalize-time text extraction
        // (finalQuickActions) — can carry the SAME chip, and a tool can emit its
        // follow-ups more than once in a turn. This primary commit path used a bare
        // concat + setPendingQuickActions with NO dedup (unlike addQuickActions,
        // which routes through appendQuickActions), so a chip present in both feeders
        // rendered twice. Dedup by label (first occurrence wins) before committing.
        const allQuickActions = dedupeQuickActionsByLabel([
          ...(meta?.quickActions || []),
          ...finalQuickActions,
        ])
        // Bug #2 (stray chips on an email-a-doc turn): when this turn surfaced an
        // email compose/draft card, any follow-up chips emitted by upstream tools
        // (e.g. the contact-group chips from search_objects resolving the
        // recipients — "Export this segment", "Tag all of them", …) are noise: the
        // user's next step is the card itself, not a segment action. Suppress ALL
        // follow-up chips — including the tour/onboarding fallbacks below — for a
        // compose-card turn. sendMessage() already cleared pending chips at turn
        // start, so skipping the commit leaves the chip row empty.
        const hasComposeCard = resultCards.some(
          (c) => (c as { kind?: string })?.kind === 'email_draft',
        )
        // Tour-chip fallback: model frequently skips the show_actions
        // tool call after a tour message even though it does call
        // focus_module. If we have a focus_module, no chips, AND the
        // last user message was a tour-step trigger, inject the next
        // chip from the static map so the user can keep advancing.
        if (!hasComposeCard && allQuickActions.length === 0) {
          // Tour-chip fallback: the model frequently skips both
          // show_actions AND focus_module on later tour steps,
          // emitting only a brief text turn. If the user's last
          // message was a recognised tour trigger and the agent
          // produced any visible response, inject the next chip
          // AND synthesize the focus_module ui_action so the user
          // can keep advancing — better an off-target chip than a
          // dead-end mid-tour.
          const trigger = content.trim().toLowerCase()
          const fallback = TOUR_CHIP_FALLBACK[trigger]
          if (fallback) {
            allQuickActions.push(fallback.chip)
            const alreadyFocused = uiActionsWithAnchors.some((a) => a?.type === 'module.focus')
            if (!alreadyFocused) {
              uiActionsWithAnchors.push({
                type: 'module.focus',
                moduleType: fallback.moduleType,
                anchorMessageId: assistantId,
              } as ChatUIAction)
            }
          }
        }
        // Onboarding chip fallback (#1718): only fires when the agent
        // is mid-onboarding AND the script genuinely stalled. We
        // consider the script "stalled" when no `XAVIOR_ACTIONS` chips
        // were emitted AND no `onboarding.advance` / `onboarding.complete`
        // UI directive was emitted. When the agent advanced state
        // (e.g. a turn that ends with an advance directive), the next
        // user reply may be free-form — injecting Yes/No/Tell-me-more
        // there would be misleading. Disabled outside onboarding so it
        // doesn't pollute normal chat.
        const onboardingAdvanced = uiActionsWithAnchors.some(
          (a) =>
            a?.type === 'onboarding.advance' ||
            a?.type === 'onboarding.complete',
        )
        if (
          !hasComposeCard &&
          allQuickActions.length === 0 &&
          !onboardingAdvanced &&
          !profileRef.current?.onboardingComplete &&
          endsWithQuestion(finalVisibleContent)
        ) {
          allQuickActions.push(
            { label: 'Yes', message: 'Yes' },
            { label: 'No', message: 'No' },
            { label: 'Tell me more', message: 'Tell me more' },
          )
        }
        if (!hasComposeCard && allQuickActions.length > 0) {
          setPendingQuickActions(allQuickActions)
        }
        if (uiActionsWithAnchors.length > 0) {
          onUiActions?.(uiActionsWithAnchors)
        }
        if (finalVisibleContent) {
          onMessage?.(assistantMessage)
        }
      }
      onComplete?.()
    }

    // Assistant message is persisted by x-agent after the agent loop — no frontend save needed
  }

  try {
    // Release the in-flight guard so subsequent messages can proceed.
    // The 60s content-dedup guard still prevents identical re-sends.
    sendInFlightRef.current = false

    if (isTargetConversationActive()) {
      // isStreaming and thinking indicator already set above (#1700);
      // reset thinking state but preserve the early activity push.
      setIsStreaming(true)
      setThinking(null)
    }
    streamingContentRef.current = ''

    // Create new abort controller for this request
    ctx.requestAbortController = new AbortController()
    abortControllerRef.current = ctx.requestAbortController
    const signal = ctx.requestAbortController.signal

    // Start the liveness clock at DISPATCH — the moment abortControllerRef.current
    // becomes non-null is exactly when the foreground-reload handler starts
    // watching this request. lastStreamEventAtRef is otherwise refreshed only on
    // the FIRST SSE event, so a visibility flip during the pre-first-frame window
    // (a slow build's TTFB) would be measured against the PREVIOUS stream's last
    // event and could abort a perfectly healthy stream. Liveness is the sole
    // abort criterion now (#5194 follow-up), so the clock must be fresh here.
    lastStreamEventAtRef.current = Date.now()

    // Stream chat events — now works on both web and Capacitor native.
    // On native, sse.ts uses the original (unpatched) window.fetch to bypass
    // CapacitorHttp and get real SSE streaming via the WebView's ReadableStream.
    let directiveRemainder = ''
    for await (const event of api.chat.openChatStream(token, request, targetConversationId, signal)) {
      const eventData = event.data as any

      // Track liveness so visibility-change handler knows the stream is active
      lastStreamEventAtRef.current = Date.now()

      // Check if streaming was aborted (belt and suspenders - signal also cancels fetch).
      // THROW rather than break: a bare `break` exits the loop cleanly and skips the
      // catch-side abort cleanup (the #5165 60s dedup-guard clear + orphan module
      // deletion), because postSse can yield buffered frames after abort WITHOUT the
      // fetch throwing. Throwing an AbortError converges this path onto the exact same
      // catch branch the fetch-throws abort already takes — the two abort paths now
      // behave identically (the break path mutates nothing, so nothing is double-applied).
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError')
      }
      if (isTargetConversationActive()) {
        setIsStreaming(true)
      }

      if (event.type === 'heartbeat') {
        // Server keep-alive (5s cadence). Reaching this point already did the
        // two things a heartbeat is FOR (#5194): `lastStreamEventAtRef` was
        // refreshed (so the foreground-reload handler knows this stream is
        // alive across a long silent build phase) and `setIsStreaming(true)`
        // was re-asserted (so the thinking indicator recovers within 5s if
        // any effect — e.g. a token-rotation refire of the session loader —
        // cleared it mid-turn). No content side effects.
        continue
      }

      if (event.type === 'connected' || event.type === 'start') {
        // Capture routing and model info from start event
        if (eventData.routing) ctx.routingInfo = eventData.routing
        if (eventData.model) ctx.modelInfo = eventData.model
        // Handle semantic routing info
        if (eventData.semanticRouting) {
          const sr = eventData.semanticRouting
          ctx.modelInfo = sr.model || ctx.modelInfo
          if (sr.tier && sr.reason) {
            ctx.routingInfo = `${sr.tier}:${sr.reason}`
          }
        }
        // Set context-aware status message from server (e.g., "Searching tasks...")
        if (eventData.message) {
          markInterleavedActivity()
          if (isTargetConversationActive()) {
            pushThinkingStatus(eventData.message)
          }
        }
        // Create empty streaming message on start
        if (isTargetConversationActive() && !hideAssistantResponse) {
          setStreamingMessage({
            id: assistantId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            streaming: true,
          } as any)
        }
        continue
      } else if (event.type === 'fast_path_response') {
        // Fast-path metadata (e.g., reason: 'person_lookup', model: 'none')
        // This is just routing info - actual text comes in separate 'text' events
        ctx.modelInfo = 'fast-path'
        ctx.routingInfo = eventData.reason || 'fast-path'
        // Don't add anything to assistantContent - the text event will do that
      } else if (event.type === 'quick-ack') {
        // Quick acknowledgment from Haiku — speak immediately in voice mode
        const ackText = String(eventData.text || '')
        if (ackText) {
          onQuickAck?.(ackText)
        }
      } else if (event.type === 'status') {
        if (eventData.message) {
          markXaviorStatusActivity()
          if (isTargetConversationActive()) {
            pushThinkingStatus(eventData.message)
          }
        }
      } else if (event.type === 'text') {
        const textContent = String(eventData.text || '')
        // extractHiddenDirectives also silently strips the legacy
        // {{XAVIOR_DIRECTIVE:...}} token — see LEGACY_STRUCTURAL_DIRECTIVE_TOKEN.
        const extracted = extractHiddenDirectives(directiveRemainder + textContent)
        directiveRemainder = extracted.remainder

        if (extracted.uiActions.length > 0) {
          queuedUiActions.push(...extracted.uiActions)
        }

        if (extracted.quickActions.length > 0) {
          queuedQuickActions.push(...extracted.quickActions)
        }

        if (extracted.statusMessages.length > 0) {
          for (const statusMessage of extracted.statusMessages) {
            markXaviorStatusActivity()
            if (isTargetConversationActive()) {
              setThinking(statusMessage)
              pushThinkingStatus(statusMessage)
            }
          }
        }

        // No visible text in this chunk (hidden directives only) — keep thinking timeline active.
        if (!extracted.visibleText) {
          continue
        }

        // Feed visible text to streaming TTS pipeline (voice mode).
        // Wrapped in try-catch to prevent voice errors from killing the SSE loop.
        try { onStreamTextChunk?.(extracted.visibleText); } catch (e) { console.warn('[useChat] onStreamTextChunk error:', e); }

        // Text is arriving:
        // - keep concrete status updates visible
        // - clear only generic placeholder thinking
        if (isTargetConversationActive() && streamActivityRef.current.length > 0) {
          const hasConcreteActivity = streamActivityRef.current.some(
            (step) => step.type !== 'thinking' || !isGenericThinkingLabel(step.label),
          )
          // Only clear the timeline when it is still the plain placeholder.
          // Keep concrete tool/status steps visible during streaming pauses.
          if (!hasConcreteActivity) {
            clearActivity()
            setThinking(null)
          }
        }

        // Accumulate and render chunks immediately as they arrive (ChatGPT style)
        // If a tool/status step just happened between text chunks, keep a short
        // paragraph boundary so phase transitions are visually readable.
        // Only force a paragraph break when the existing content already ends
        // on a real sentence terminator. Otherwise an interleaved tool/status
        // event mid-sentence would inject \n\n into "Ha" + ", noted..." and the
        // chat renderer would split a single sentence across two visible chunks.
        const lastChar = ctx.assistantContent.trimEnd().slice(-1)
        const endsOnTerminator = lastChar === '.' || lastChar === '!' || lastChar === '?' || lastChar === '\n'
        const shouldInsertParagraphBreak =
          ctx.sawInterleavedActivitySinceLastText && ctx.assistantContent.trim().length > 0 && endsOnTerminator
        if (ctx.sawXaviorStatusSinceLastText && ctx.assistantContent.trim().length > 0) {
          await waitForXaviorStatusVisibility(ctx.lastXaviorStatusAtMs, signal)
        }
        ctx.assistantContent = appendAssistantChunk(ctx.assistantContent, extracted.visibleText, {
          forceParagraphBreak: shouldInsertParagraphBreak,
        })
        ctx.sawInterleavedActivitySinceLastText = false
        ctx.sawXaviorStatusSinceLastText = false

        // Update streaming message immediately - the natural chunk timing creates smoothness
        if (isTargetConversationActive() && !hideAssistantResponse) {
          setStreamingMessage({
            id: assistantId,
            role: 'assistant',
            content: ctx.assistantContent,
            timestamp: new Date(),
            thinking: assistantThinking || undefined,
            streaming: true,
          } as any)
        }
      } else if (event.type === 'text_reset') {
        // The server discarded speculative pre-tool text (the model re-emits its
        // answer after the tool result, which otherwise double-renders). Roll the
        // in-progress assistant bubble back to the last tool-boundary snapshot.
        ctx.assistantContent = ctx.committedContent
        ctx.sawInterleavedActivitySinceLastText = false
        ctx.sawXaviorStatusSinceLastText = false
        if (isTargetConversationActive() && !hideAssistantResponse) {
          setStreamingMessage({
            id: assistantId,
            role: 'assistant',
            content: ctx.assistantContent,
            timestamp: new Date(),
            thinking: assistantThinking || undefined,
            streaming: true,
          } as any)
        }
      } else if (event.type === 'tool_start') {
        toolCalls.push(...(eventData.toolCalls || []))
        // Show specific tool labels (e.g., "Creating task...") using tool params
        const toolCalls_ = eventData.toolCalls || []
        const toolNames = toolCalls_.map((tc: any) => tc.name).filter(Boolean)
        // Capture objectType for any create_object call so the matching
        // result can fire a targeted list-query invalidation (#2458).
        // Key by tool_use id when available so parallel/retried calls
        // don't mis-correlate on out-of-order results.
        for (const tc of toolCalls_ as Array<{ name?: string; params?: Record<string, unknown>; id?: string }>) {
          const params = tc?.params || {}
          const action = typeof params.action === 'string' ? params.action : undefined
          const objectType = typeof params.objectType === 'string' ? params.objectType : undefined
          // Track create_object AND update_object (amends) so the initiator —
          // e.g. the Daily Brief — can confirm "<destination> updated" for a
          // rescheduled/edited event, not just a freshly created one.
          if (isTrackedMutation(action) && objectType) {
            pendingCreates.register(tc.id, objectType, ctx.createOrdinal++)
          }
        }
        if (toolNames.length > 0) {
          markInterleavedActivity()
          if (isTargetConversationActive()) {
            const tc = toolCalls_[0] as { name?: string; params?: Record<string, unknown> }
            const label = getToolLabel(tc?.name || toolNames[0], tc?.params)
            // Always suppress uninformative generic labels ("Running...",
            // "Working...") — keep the previous, more specific status shimmering
            // instead of flashing a vague verb.
            if (!isUninformativeToolLabel(label)) {
              setThinking(label)
              pushActivity(label, 'tool_start')
            }
          }
        }
        // Signal that the agent is using tools (likely modifying data)
        onToolActivity?.()
      } else if (event.type === 'tool_receipt') {
        ctx.sawReceipt = true
        toolResults.push({ ...eventData, receiptType: 'receipt' })
        if (Array.isArray(eventData.uiActions)) {
          queuedUiActions.push(...(eventData.uiActions as ChatUIAction[]))
        }
        ctx.toolsUsedCount++

        // Agent-action UI effects (Phase D): when the payload is an enriched
        // wire receipt, validate it and dispatch typed effects into the
        // agent-effects store. Each effect's resource type also drives the same
        // targeted list-query invalidation a create_object receipt fires, so the
        // touched module list refreshes promptly. The legacy fields path below
        // remains the fallback when `wire` is null (old/unflagged agents).
        const wire = parseAgentActionReceipt(eventData)
        if (wire) {
          ingestAgentReceipt?.(wire)
          for (const effect of wire.effects) {
            // A `deleted` effect highlights the row that is about to vanish (red
            // wash + `agent-fx-deleting`). Firing the list invalidation IMMEDIATELY
            // here refetches the (already server-deleted) list and unmounts that
            // very chip in the same tick the effect is ingested — the delete
            // highlight then either flashes for a single frame or never paints at
            // all. The race is weighted by how many chips disappear: a single
            // delete sometimes wins the paint, but a whole-SERIES delete removes
            // EVERY occurrence chip in one refetch pass, so the master-keyed delete
            // highlight is never seen. Defer the delete refetch by a short grace
            // window so the chip stays mounted long enough to paint the highlight,
            // then the refetch removes it and `<AnimatePresence>` plays the collapse
            // exit. Non-delete effects (create/update/move) keep the immediate
            // invalidation — they re-render the SAME chip, no removal race.
            if (effect.kind === 'deleted') {
              const resourceType = effect.resource.type
              setTimeout(
                () => scheduleObjectListInvalidation(queryClient, resourceType),
                DELETE_HIGHLIGHT_GRACE_MS,
              )
            } else {
              scheduleObjectListInvalidation(queryClient, effect.resource.type)
            }
          }
        }

        // Show clean status labels — suppress verbose raw summaries for read-only tools.
        // Derive ok/label from the validated wire shape when present; otherwise
        // fall back to the legacy receipt fields (receipt.ok / receipt.summary).
        const receipt = eventData
        const receiptToolName = wire
          ? wire.tool.name
          : typeof receipt.tool === 'string' ? receipt.tool : ''
        const receiptOk = wire ? wire.status === 'succeeded' : Boolean(receipt.ok)
        const receiptSummary = wire ? wire.summary?.safeText : receipt.summary
        const isReadOnlyTool = /^(search|list_|get_|read_|recall_|places_|flight_|aggregate_)/.test(receiptToolName)
        if (receiptSummary && !isReadOnlyTool) {
          markInterleavedActivity()
          if (isTargetConversationActive()) {
            const label = receiptOk ? receiptSummary : `Error: ${receiptSummary}`
            setThinking(label)
            pushActivity(label, receiptOk ? 'tool_done' : 'tool_error')
          }
        }
        // Notify shimmer animation for newly created objects (legacy id path).
        if (receiptOk && typeof receipt.id === 'string' && receipt.id) {
          onObjectCreated?.(receipt.id)
        }
        // #2458: targeted list-query invalidation for create_object calls.
        // The generic onToolActivity invalidation fires on a 500ms debounce
        // which races background enrichment (e.g. place auto-enrich, ~2-5s
        // for Google Places lookup + R2 photo upload). scheduleObjectListInvalidation
        // fires the immediate + ~6s follow-up pair so enrichment is picked
        // up promptly. See pendingCreateRegistry.ts for the rationale and
        // ENRICHMENT_FOLLOWUP_MS value.
        //
        // Receipts may carry a `toolUseId` (or `id`) that matches the
        // originating tool_start; prefer id-keyed consume to keep
        // parallel/retried create_object calls correlated.
        if (receiptOk && pendingCreates.size() > 0) {
          const receiptToolUseId = typeof receipt.toolUseId === 'string'
            ? receipt.toolUseId
            : typeof receipt.id === 'string' ? receipt.id : undefined
          // consumeAny() only when no id is present (legacy id-less agents); a
          // present-but-non-matching id belongs to another tool, not this create.
          const objectType = pendingCreates.consume(receiptToolUseId)
            ?? (receiptToolUseId ? undefined : pendingCreates.consumeAny())
          if (objectType) {
            createdObjectTypes.push(objectType)
            scheduleObjectListInvalidation(queryClient, objectType)
          }
        }
        // Tool completed — data likely changed, refresh UI
        onToolActivity?.()
      } else if (event.type === 'tool_result') {
        if (!ctx.sawReceipt) {
          toolResults.push({ ...eventData, receiptType: 'result' })
        }
        ctx.toolsUsedCount++
        // Notify shimmer animation for newly created objects.
        // Prefer the server-provided createdObjectId (extracted pre-sanitization),
        // falling back to parsing the sanitized result for backward compatibility.
        const serverId = (eventData as { createdObjectId?: unknown })?.createdObjectId
        const createdId = (typeof serverId === 'string' ? serverId : undefined)
          ?? extractCreatedObjectId(eventData?.result ?? eventData)
        if (createdId) onObjectCreated?.(createdId)
        // #2458 + note-create fix: targeted list-query invalidation when a
        // create_object tool_result arrives without a separate tool_receipt
        // (the x-agent emits tool_result, not tool_receipt). This must NOT
        // depend on createdId — many create_object results are a plain-text
        // llmContent string (e.g. notes: `create succeeded: "X" (note id: …)`)
        // that extractCreatedObjectId can't parse, so requiring createdId
        // silently skipped the refetch and the new object never appeared in its
        // module list until a manual reload. pendingCreates already captured the
        // objectType at tool_start, which is all we need to refresh the right
        // list; createdId stays scoped to the onObjectCreated shimmer above.
        const resultIsError = Boolean((eventData as { isError?: unknown }).isError)
        if (!ctx.sawReceipt && !resultIsError && pendingCreates.size() > 0) {
          const resultToolUseId = typeof (eventData as { toolUseId?: unknown }).toolUseId === 'string'
            ? (eventData as { toolUseId: string }).toolUseId
            : undefined
          // consumeAny() only when no id is present (legacy id-less agents); a
          // non-create tool_result carries a non-matching id and must not drain a
          // pending create (which would invalidate the wrong — or a failed — list).
          const objectType = pendingCreates.consume(resultToolUseId)
            ?? (resultToolUseId ? undefined : pendingCreates.consumeAny())
          if (objectType) {
            createdObjectTypes.push(objectType)
            scheduleObjectListInvalidation(queryClient, objectType)
            // Agent-action UI effects (Phase D): feed the new store a TYPED
            // created effect so the create-glow fires even when the x-agent
            // emits tool_result (no enriched wire receipt). Mirrors the legacy
            // onObjectCreated call above, but carries the resolved objectType.
            if (createdId) markAgentCreatedEffect?.(objectType, createdId)
          }
        }
        // Tool completed — data likely changed, refresh UI
        onToolActivity?.()
        // Advance the rollback snapshot: text streamed AFTER this tool boundary
        // is the model's real post-tool answer, so a later text_reset must not
        // discard it.
        ctx.committedContent = ctx.assistantContent
      } else if (event.type === 'ui-action') {
        // Handle report.* / html.* UI actions streamed mid-response. The full
        // switch lives in applyUiDirective (core B); it mutates the SAME ctx by
        // reference and may throw AbortError mid-create — which propagates to
        // the catch below for orphan cleanup, exactly as the inline switch did.
        const action = eventData as { type: string; [key: string]: unknown }
        await applyUiDirective(ctx, action, deps, signal)
      } else if (event.type === 'complete' || event.type === 'close') {
        // Clear focusedSections at end-of-turn so turn 2 starts fresh.
        // This is the deferred clear (was previously firing eagerly at
        // send-time, which created a mid-stream race — see comment at
        // line ~1900). Defending the in-flight turn while clearing for
        // future turns is the goal; running it here satisfies both.
        // Only fires on successful complete/close, NOT on errors —
        // letting the user retry with the same selection on transient
        // network failures.
        if (focusedSectionsRef.current.length > 0) {
          setFocusedSections([])
        }
        if (directiveRemainder) {
          const trailing = extractHiddenDirectives(directiveRemainder)
          directiveRemainder = trailing.remainder
          if (trailing.uiActions.length > 0) {
            queuedUiActions.push(...trailing.uiActions)
          }
          if (trailing.quickActions.length > 0) {
            queuedQuickActions.push(...trailing.quickActions)
          }
          if (trailing.statusMessages.length > 0) {
            for (const statusMessage of trailing.statusMessages) {
              markXaviorStatusActivity()
              if (isTargetConversationActive()) {
                setThinking(statusMessage)
                pushThinkingStatus(statusMessage)
              }
            }
          }
          if (trailing.visibleText) {
            // Feed trailing text to streaming TTS before accumulation
            try { onStreamTextChunk?.(trailing.visibleText); } catch (_e) { /* ignore */ }
            // Same terminator-only paragraph-break rule as the primary
            // text path — don't split mid-sentence on a trailing flush.
            const trailingLastChar = ctx.assistantContent.trimEnd().slice(-1)
            const trailingEndsOnTerminator = trailingLastChar === '.' || trailingLastChar === '!' || trailingLastChar === '?' || trailingLastChar === '\n'
            const shouldInsertParagraphBreak =
              ctx.sawInterleavedActivitySinceLastText && ctx.assistantContent.trim().length > 0 && trailingEndsOnTerminator
            if (ctx.sawXaviorStatusSinceLastText && ctx.assistantContent.trim().length > 0) {
              await waitForXaviorStatusVisibility(ctx.lastXaviorStatusAtMs, signal)
            }
            ctx.assistantContent = appendAssistantChunk(ctx.assistantContent, trailing.visibleText, {
              forceParagraphBreak: shouldInsertParagraphBreak,
            })
            ctx.sawInterleavedActivitySinceLastText = false
            ctx.sawXaviorStatusSinceLastText = false
          }
        }
        if (ctx.sawXaviorStatusSinceLastText) {
          await waitForXaviorStatusVisibility(ctx.lastXaviorStatusAtMs, signal)
        }
        ctx.sawInterleavedActivitySinceLastText = false
        ctx.sawXaviorStatusSinceLastText = false
        // Use server-side duration if available, otherwise calculate client-side
        if (Array.isArray(eventData?.uiActions)) {
          queuedUiActions.push(...(eventData.uiActions as ChatUIAction[]))
        }
        ctx.responseDurationMs = eventData?.stats?.durationMs
          || eventData?.stats?.duration_ms
          || (Date.now() - requestStartTime)
        // Refresh sidebar title if the server provided a conversation title (#1699)
        if (eventData?.conversationTitle && typeof eventData.conversationTitle === 'string') {
          queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists() })
        }
        // Finalize active report modules: set status to 'complete' and persist
        if (ctx.activeReportModules.size > 0) {
          let lastReportModuleId: string | undefined
          for (const [id, report] of ctx.activeReportModules) {
            report.config.status = 'complete'
            report.config.statusMessage = undefined
            window.dispatchEvent(new CustomEvent('xavior:report-config-update', {
              detail: { moduleId: id, config: { ...report.config, sections: [...report.config.sections] } },
            }))
            try {
              await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
                config: report.config as unknown as Record<string, unknown>,
              })
              lastReportModuleId = id
            } catch (err) {
              console.error('[useChat] Failed to finalize report module:', err)
            }
          }
          // End-of-report CTA (#3793): derive the kind:'report' result.card(s)
          // NOW, while the registry is still populated — finalizeAssistantMessage
          // runs after this block, so deriving there always saw an empty map and
          // the card never fired on a successful build (PR #3966 review).
          // queuedUiActions already holds every agent-emitted AND
          // text-directive-extracted ui action for this turn (the stream loop
          // pushes both before 'complete'), so deduping against it also covers
          // the sanitizedFinal.uiActions re-extraction in assembleFinalizeContent
          // (finalContent has had its directives stripped during streaming).
          // Only this successful-complete path cards: ≥1 rendered section AND a
          // finalize that represents a delivered turn. Abort, disconnect-with-
          // partial, and the non-streaming error-fallback paths intentionally do
          // NOT card — those reports are partial/errored (dead CTA otherwise).
          queuedUiActions.push(
            ...deriveReportResultCardActions(ctx.activeReportModules, queuedUiActions),
          )
          ctx.activeReportModules.clear()
          // Re-focus the report module now that streaming is done — the mid-stream
          // focus skipped invalidation, so this ensures full query sync
          if (lastReportModuleId) {
            queuedUiActions.push({
              type: 'module.focus',
              moduleId: lastReportModuleId,
              anchorMessageId: assistantId,
            } as ChatUIAction)
          }
          onToolActivity?.()
        }
        // End-of-artifact CTA: derive kind:'artifact' result.card(s) while the
        // active-artifact registry is still populated. Mirrors the report CTA
        // block above. Only fires on successful complete (partial/abort paths
        // don't card — the registry stays populated but the content is incomplete).
        if (ctx.activeArtifacts.size > 0) {
          queuedUiActions.push(
            ...deriveArtifactCardActions(ctx.activeArtifacts, queuedUiActions),
          )
          ctx.activeArtifacts.clear()
        }
        // Finalize active HTML modules (safety net if html.complete never arrived)
        if (ctx.activeHtmlModules.size > 0) {
          let lastHtmlModuleId: string | undefined
          let safetyNetPersistFailed = false
          for (const [id, mod] of ctx.activeHtmlModules) {
            setLiveHtmlState(id, mod.html, true)
            const dispatchId = toCanonicalPageId(id, activePageContextRef.current) ?? id
            window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
              detail: { moduleId: dispatchId, html: mod.html, final: true },
            }))
            try {
              await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
                config: { html: mod.html } as unknown as Record<string, unknown>,
              })
              lastHtmlModuleId = id
            } catch (err) {
              console.error('[useChat] Failed to finalize html module:', err)
              safetyNetPersistFailed = true
            }
          }
          ctx.activeHtmlModules.clear()
          if (safetyNetPersistFailed && isTargetConversationActive()) {
            // Surface the failure so the user knows their page wasn't saved,
            // instead of showing an empty "No content to display" on reopen.
            setError(new Error('Page built but failed to save — try again.'))
            onError?.(new Error('Page built but failed to save — try again.'))
          }
          // Re-focus without skipInvalidation so the modules query refetches
          // and config.html catches up to liveHtml in the viewer.
          if (lastHtmlModuleId) {
            queuedUiActions.push({
              type: 'module.focus',
              moduleType: 'pages',
              anchorMessageId: assistantId,
            } as ChatUIAction)
          }
          onToolActivity?.()
        }
        // Silent-stop detection: if the agent emitted html.status messages
        // (i.e. it was actively building a page) but NEVER successfully
        // created an html_viewer module AND emitted no real text reply,
        // the build crashed mid-stream — usually a maxTokens hit on long
        // pages. Surface a clear error instead of leaving the user staring
        // at status messages that just stopped.
        //
        // We use `htmlModuleEverCreated` (set in html.init success path)
        // rather than `activeHtmlModules.size === 0` because the safety-net
        // finalize loop above clears the map on legitimate successful
        // builds, which would false-positive the check.
        //
        // 12-char threshold (down from 30) avoids tripping on legit short
        // clarification replies like "What's the venue?" while still
        // catching empty/whitespace replies after a build crash.
        const noHtmlEverMaterialized = !ctx.htmlModuleEverCreated
        const trivialContent = ctx.assistantContent.trim().length < 12
        if (ctx.sawHtmlStatusMessage && noHtmlEverMaterialized && trivialContent) {
          const buildFailedMsg = "I started building your page but the build was cut off before any sections were finished. This usually happens on very large pages — try asking for a simpler version with fewer sections, or focus on one section at a time."
          ctx.assistantContent = ctx.assistantContent
            ? `${ctx.assistantContent}\n\n${buildFailedMsg}`
            : buildFailedMsg
          if (isTargetConversationActive()) {
            setError(new Error('Page build was cut off'))
            onError?.(new Error('Page build was cut off'))
          }
        }

        // Pass raw assistantContent (may be empty). finalizeAssistantMessage
        // decides whether to suppress the bubble (tool-only turn) or show the
        // "No response" fallback (no text AND no actions).
        finalizeAssistantMessage(ctx.assistantContent, {
          durationMs: ctx.responseDurationMs,
          routing: ctx.routingInfo || undefined,
          model: ctx.modelInfo || undefined,
          uiActions: queuedUiActions,
          quickActions: queuedQuickActions.length > 0 ? queuedQuickActions : undefined,
        })
        // Stop processing further events: some servers emit both 'complete' and
        // 'close' for one stream, which would re-run module finalize side effects
        // (duplicate updateWorkspaceModule, duplicate module.focus enqueue).
        break
      }
      // Note: event.type === 'error' is now handled inside postSse (sse.ts),
      // which throws a structured Error that propagates to the outer catch
      // below. See Task 5 of the UsageLimitModal plan.
    }

    if (isTargetConversationActive()) {
      setThinking(null)
      clearActivity()
    }
  } catch (err) {
    const error = err instanceof Error ? err : new Error('Failed to send message')

    // If aborted (user-stop OR superseded by a new sendMessage), do not commit
    // any partial assistant content. Committing partials here would create stale
    // duplicate bubbles when the user fires a follow-up before the previous
    // stream finished. The new stream will produce the canonical reply.
    //
    // BEFORE returning, clean up any orphaned modules created during this
    // aborted run so they don't sit blank in the Pages list forever.
    if (error.name === 'AbortError') {
      console.log('[useChat] Request aborted — discarding partial content and cleaning up orphans')
      // #5165: clear the 60s identical-content dedup guard. finalize (which
      // normally clears it) never runs on abort, so without this the user's
      // natural retry — re-sending the same build prompt, which the composer
      // even repopulates after a quick Stop — was silently dropped by
      // isDuplicateContentSend for a full minute (no request, no feedback).
      // An aborted turn produces no reply, so there is nothing to dedup against.
      // Only clear OUR entry: a newer send may have already replaced the ref
      // (rapid A→B, then Stop aborts A) and B's guard must survive A's abort.
      if (lastSendRef.current?.content === content) {
        lastSendRef.current = null
      }
      // Empty html_viewer modules: delete entirely
      if (ctx.activeHtmlModules.size > 0) {
        for (const [id, mod] of ctx.activeHtmlModules) {
          if (!mod.html) {
            try {
              await api.workspaces.deleteWorkspaceModule(token, workspaceId, id)
              clearLiveHtmlState(id)
            } catch (delErr) {
              console.error('[useChat] Failed to delete orphan html on abort:', delErr)
            }
          }
          // For non-empty html modules: leave them in DB with whatever
          // partial content they have — user can find them in the Pages
          // list. Don't dispatch final:true because the abort means the
          // build is incomplete and the user is moving on.
        }
        ctx.activeHtmlModules.clear()
      }
      // Empty report modules: delete; partial reports stay (same logic).
      if (ctx.activeReportModules.size > 0) {
        for (const [id, report] of ctx.activeReportModules) {
          if (report.config.sections.length === 0) {
            try {
              await api.workspaces.deleteWorkspaceModule(token, workspaceId, id)
            } catch (delErr) {
              console.error('[useChat] Failed to delete orphan report on abort:', delErr)
            }
          }
        }
        ctx.activeReportModules.clear()
      }
      ctx.activeArtifacts.clear()
      ctx.activeArtifactId = undefined
      return
    }

    // Don't retry on auth/payment/rate-limit/too-large errors. A 413
    // (#5548) is deterministic for a given body — retrying just re-sends the
    // same over-ceiling payload and 413s again (the "repeating 413" the user
    // saw), so surface the friendly message and stop.
    const errStatus = (error as Error & { status?: number }).status
    if (errStatus === 401 || errStatus === 402 || errStatus === 429 || errStatus === 413) {
      const userFriendlyError = getUserFriendlyErrorMessage(error)
      if (isTargetConversationActive()) {
        setError(userFriendlyError)
        onError?.(userFriendlyError)
      }
      return
    }

    // Layer 3 ("No response" recovery): surface a transient retry bubble
    // when the stream truly produced nothing. The bubble is NOT persisted;
    // it lives in `retryBubble` state until the user retries or sends
    // again. Only the user's original message was persisted.
    const surfaceRetryBubble = (reason: string) => {
      if (!isTargetConversationActive() || hideAssistantResponse) return
      setRetryBubble({
        id: `retry-${assistantId}`,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        deliveryStatus: 'failed',
        retryPrompt: content.trim(),
        routing: reason,
      })
    }

    const hasPartial = ctx.assistantContent.trim().length > 0
    if (!ctx.messageFinalized && !hasPartial) {
      try {
        const fallback = await api.chat.postChat(token, request, targetConversationId, undefined)
        const fallbackDurationMs = fallback.stats?.durationMs ?? (fallback.stats as any)?.duration_ms
        ctx.responseDurationMs = fallbackDurationMs
          ? Number(fallbackDurationMs)
          : (Date.now() - requestStartTime)
        ctx.toolsUsedCount = fallback.toolResults?.length || ctx.toolsUsedCount
        ctx.routingInfo = typeof fallback.routing === 'string'
          ? fallback.routing
          : (fallback.routing as any)?.reason || ctx.routingInfo
        ctx.modelInfo = (fallback.routing as any)?.model || ctx.modelInfo
        if (fallback.toolCalls?.length) {
          toolCalls.push(...fallback.toolCalls)
        }
        if (fallback.toolResults?.length) {
          toolResults.push(...fallback.toolResults)
        }
        const fallbackExtracted = extractHiddenDirectives(String(fallback.text || ''))
        const mergedFallbackUiActions: ChatUIAction[] = [
          ...((fallback.uiActions || []) as ChatUIAction[]),
          ...fallbackExtracted.uiActions,
        ]

        // True-empty check: fallback returned nothing we can turn into a
        // message and the stream didn't do any tool work either. This is
        // the case the old `text-or-literal` fallback was papering over.
        // Fire the transient retry affordance instead of persisting any
        // literal.
        const fallbackHasText = Boolean(fallbackExtracted.visibleText?.trim())
        const hadTools = ctx.toolsUsedCount > 0 || toolCalls.length > 0
        const hadDirectiveActivity =
          mergedFallbackUiActions.length > 0 || queuedUiActions.length > 0
        if (!fallbackHasText && !hadTools && !hadDirectiveActivity) {
          // Diagnostics: capture the empty turn so an intermittent prod
          // occurrence is traceable to its conversation + payload.
          console.warn('[useChat] empty-response: stream and non-streaming fallback both returned nothing', {
            conversationId: targetConversationId,
            durationMs: ctx.responseDurationMs,
            routing: ctx.routingInfo,
            model: ctx.modelInfo,
            fallbackTextLength: String(fallback.text || '').length,
            streamPartialLength: ctx.assistantContent.length,
            toolsUsedCount: ctx.toolsUsedCount,
            toolCalls: toolCalls.length,
          })
          surfaceRetryBubble('empty-response')
          return
        }

        finalizeAssistantMessage(fallbackExtracted.visibleText, {
          durationMs: ctx.responseDurationMs,
          routing: ctx.routingInfo || undefined,
          model: ctx.modelInfo || undefined,
          uiActions: mergedFallbackUiActions,
        })
        return
      } catch (fallbackError) {
        console.error('[useChat] Non-streaming fallback failed:', fallbackError)
        // Both streaming and the non-streaming fallback failed. If the
        // stream never produced ANY tool work either, render a transient
        // retry bubble (layer 3) — never persist a literal.
        const hadTools = ctx.toolsUsedCount > 0 || toolCalls.length > 0
        const hadDirectiveActivity = queuedUiActions.length > 0
        if (!hadTools && !hadDirectiveActivity) {
          // Diagnostics: both the stream and the fallback POST threw. Record
          // the error + conversation so intermittent prod drops are traceable.
          console.warn('[useChat] connection-lost: stream and non-streaming fallback both failed', {
            conversationId: targetConversationId,
            fallbackError: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
            durationMs: Date.now() - requestStartTime,
            streamPartialLength: ctx.assistantContent.length,
          })
          surfaceRetryBubble('connection-lost')
          return
        }
      }
    } else if (!ctx.messageFinalized && hasPartial) {
      // If report modules were being built when the stream died,
      // ensure a module.focus action is queued so the panel opens
      if (ctx.activeReportModules.size > 0) {
        for (const [id] of ctx.activeReportModules) {
          queuedUiActions.push({
            type: 'module.focus',
            moduleId: id,
            anchorMessageId: assistantId,
          } as ChatUIAction)
        }
        // Mark failed report modules as error so they don't stay in 'building' forever
        for (const [id, report] of ctx.activeReportModules) {
          report.config.status = 'error'
          report.config.statusMessage = 'Connection lost during report generation'
          window.dispatchEvent(new CustomEvent('xavior:report-config-update', {
            detail: { moduleId: id, config: { ...report.config } },
          }))
          try {
            await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
              config: report.config as unknown as Record<string, unknown>,
            })
          } catch (err) {
            console.error('[useChat] Failed to mark report as error:', err)
          }
        }
        ctx.activeReportModules.clear()
      }
      ctx.activeArtifacts.clear()
      ctx.activeArtifactId = undefined
      ctx.responseDurationMs = Date.now() - requestStartTime
      finalizeAssistantMessage(ctx.assistantContent, {
        durationMs: ctx.responseDurationMs,
        routing: ctx.routingInfo || undefined,
        model: ctx.modelInfo || undefined,
        uiActions: queuedUiActions,
      })
      return
    }

    // Mark any orphaned report modules as error
    if (ctx.activeReportModules.size > 0) {
      for (const [id, report] of ctx.activeReportModules) {
        report.config.status = 'error'
        report.config.statusMessage = 'Report generation failed'
        window.dispatchEvent(new CustomEvent('xavior:report-config-update', {
          detail: { moduleId: id, config: { ...report.config } },
        }))
        try {
          await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
            config: report.config as unknown as Record<string, unknown>,
          })
        } catch (err) {
          console.error('[useChat] Failed to mark report as error:', err)
        }
      }
      ctx.activeReportModules.clear()
    }

    // Persist any partial HTML, OR delete the empty shell module so we
    // don't leave a blank entry in the Pages list.
    if (ctx.activeHtmlModules.size > 0) {
      for (const [id, mod] of ctx.activeHtmlModules) {
        if (mod.html) {
          setLiveHtmlState(id, mod.html, true)
          const dispatchId = toCanonicalPageId(id, activePageContextRef.current) ?? id
          window.dispatchEvent(new CustomEvent('xavior:html-config-update', {
            detail: { moduleId: dispatchId, html: mod.html, final: true },
          }))
          try {
            await api.workspaces.updateWorkspaceModule(token, workspaceId, id, {
              config: { html: mod.html } as unknown as Record<string, unknown>,
            })
          } catch (err) {
            console.error('[useChat] Failed to save partial html module:', err)
          }
        } else {
          // html.init created the shell but no chunks ever arrived.
          // Delete the orphan so it doesn't appear blank in Pages.
          // Invariant: focus is deferred until first body chunk, so
          // mod.focused === false here — no dangling module.focus action
          // was ever dispatched for this orphan, deletion is silent.
          try {
            await api.workspaces.deleteWorkspaceModule(token, workspaceId, id)
            clearLiveHtmlState(id)
          } catch (err) {
            console.error('[useChat] Failed to delete empty html module:', err)
          }
        }
      }
      ctx.activeHtmlModules.clear()
    }
    ctx.activeArtifacts.clear()
    ctx.activeArtifactId = undefined

    const userFriendlyError = getUserFriendlyErrorMessage(error)
    if (isTargetConversationActive()) {
      setError(userFriendlyError)
      onError?.(userFriendlyError)
    }
    console.error('[useChat] Error:', error.message, '→', userFriendlyError.message)
  } finally {
    if (isTargetConversationActive()) {
      setIsStreaming(false)
      setIsLoading(false)
      setStreamingMessage(null)
      clearActivity()
    }
    if (ctx.requestAbortController && abortControllerRef.current === ctx.requestAbortController) {
      abortControllerRef.current = null
    }
  }
}
